import { useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useVoiceWS } from '../hooks/useVoiceWS'
import { Icon } from './Icon'

// ─── VoicePanel ──────────────────────────────────────────────────────────────
//
// Live voice UI bound to a single workspace + chat. Renders a mic toggle, the
// rolling transcript (user + assistant), a "speaking…" indicator while TTS plays
// and a chip showing the latest server-side tool status.
//
// All audio + protocol work lives in useVoiceWS; this component is presentation
// only. Styling reuses the app's CSS variables where possible and otherwise uses
// minimal inline styles so it doesn't depend on new global.css classes.

interface VoicePanelProps {
  workspaceId?: string
  chatId?:      string
  /** Hide the panel (header close button). */
  onClose:      () => void
}

export function VoicePanel({ workspaceId, chatId, onClose }: VoicePanelProps) {
  const { token } = useAuth()
  const { state, transcripts, toolStatus, error, start, stop } =
    useVoiceWS(token, { workspaceId, chatId })

  const listening = state === 'listening' || state === 'speaking'
  const transcriptEndRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll the transcript to the newest line.
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [transcripts])

  const toggleMic = () => {
    if (listening || state === 'connecting') stop()
    else start()
  }

  const stateLabel: Record<string, string> = {
    idle:       'Tap to talk',
    connecting: 'Connecting…',
    listening:  'Listening…',
    speaking:   'Speaking…',
    error:      'Error',
  }

  return (
    <div
      style={{
        display:       'flex',
        flexDirection: 'column',
        height:        '100%',
        background:    'var(--bg)',
        borderLeft:    '1px solid var(--border)',
      }}
    >
      {/* Header */}
      <div
        className="chat-header"
        style={{ gap: '8px' }}
      >
        <span
          className="chat-header-dot"
          style={{ color: state === 'error' ? 'var(--red, #ff7b72)' : 'var(--accent)' }}
        >
          ●
        </span>
        <span className="chat-header-name">Voice</span>
        <span className="chat-header-state">{stateLabel[state] ?? state}</span>

        {toolStatus && (
          <span
            className="chat-session-badge"
            title={toolStatus.summary ?? toolStatus.name}
            style={{ opacity: toolStatus.status === 'done' ? 0.7 : 1 }}
          >
            🔧 {toolStatus.name}
            {toolStatus.status === 'start' ? '…' : ' ✓'}
          </span>
        )}

        <div className="chat-header-spacer" />
        <button
          onClick={onClose}
          className="btn-ghost chat-settings-btn"
          title="Close voice panel"
          aria-label="Close voice panel"
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding:    '8px 16px',
            fontSize:   '12px',
            color:      'var(--red, #ff7b72)',
            background: 'color-mix(in srgb, var(--red, #ff7b72) 12%, transparent)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {error}
        </div>
      )}

      {/* Transcript */}
      <div
        style={{
          flex:      1,
          overflowY: 'auto',
          padding:   '16px',
          display:   'flex',
          flexDirection: 'column',
          gap:       '10px',
          minHeight: 0,
        }}
      >
        {transcripts.length === 0 ? (
          <div
            style={{
              color:     'var(--text-muted)',
              fontSize:  '13px',
              textAlign: 'center',
              marginTop: '24px',
            }}
          >
            Press the mic and start speaking.
          </div>
        ) : (
          transcripts.map((t, i) => (
            <div
              key={i}
              style={{
                alignSelf:    t.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth:     '85%',
                padding:      '8px 12px',
                borderRadius: '12px',
                fontSize:     '13px',
                lineHeight:   1.4,
                color:        'var(--text)',
                background:   t.role === 'user'
                  ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
                  : 'var(--bg-elevated, #161b22)',
                border:       '1px solid var(--border)',
                opacity:      t.final ? 1 : 0.7,
                fontStyle:    t.final ? 'normal' : 'italic',
              }}
            >
              {t.text}
            </div>
          ))
        )}
        <div ref={transcriptEndRef} />
      </div>

      {/* Speaking indicator */}
      {state === 'speaking' && (
        <div
          style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            gap:            '6px',
            padding:        '6px',
            fontSize:       '12px',
            color:          'var(--accent)',
          }}
        >
          <span>Speaking</span>
          <span className="thinking-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      )}

      {/* Mic control */}
      <div
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          padding:        '16px',
          borderTop:      '1px solid var(--border)',
        }}
      >
        <button
          onClick={toggleMic}
          aria-label={listening ? 'Stop voice' : 'Start voice'}
          aria-pressed={listening}
          disabled={!token}
          style={{
            width:          '64px',
            height:         '64px',
            borderRadius:   '50%',
            border:         'none',
            cursor:         token ? 'pointer' : 'not-allowed',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            background: listening
              ? 'linear-gradient(135deg, #3a3a3a, #2a2a2a)'
              : 'linear-gradient(135deg, #2a2a2a, #1c1c1c)',
            boxShadow: listening
              ? '0 0 0 3px rgba(255, 255, 255, 0.25), 0 4px 20px rgba(255, 255, 255, 0.12)'
              : '0 2px 12px rgba(0, 0, 0, 0.5)',
            transition:        'background 0.2s ease, box-shadow 0.2s ease',
            touchAction:       'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Microphone icon (inline SVG — matches VoiceButton.tsx) */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9" y="2" width="6" height="11" rx="3" fill={listening ? '#ececec' : '#9b9b9b'} />
            <path
              d="M5 11C5 14.866 8.13401 18 12 18C15.866 18 19 14.866 19 11"
              stroke={listening ? '#ececec' : '#9b9b9b'}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line x1="12" y1="18" x2="12" y2="22" stroke={listening ? '#ececec' : '#9b9b9b'} strokeWidth="2" strokeLinecap="round" />
            <line x1="8" y1="22" x2="16" y2="22" stroke={listening ? '#ececec' : '#9b9b9b'} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}
